import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AttachFile as AttachFileIcon,
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import {
  formatFileSize,
  isImageFile,
  isPdfFile,
  isPreviewableFile,
  isVideoFile,
} from "../../../utils/fileUtils";

const ALLOWED_EXTENSIONS = [
  ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".txt", ".csv", ".rtf",
  ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp", ".heic", ".heif", ".svg",
  ".mp4", ".mov", ".avi", ".wmv", ".mkv", ".webm", ".3gp", ".m4v",
  ".mp3", ".wav", ".m4a", ".aac", ".ogg",
  ".zip", ".rar", ".7z",
];

const getExtension = (fileName: string): string =>
  fileName.slice(fileName.lastIndexOf(".")).toLowerCase();

const isFileTypeAllowed = (file: File): boolean =>
  ALLOWED_EXTENSIONS.includes(getExtension(file.name));

const formatCategory = (category: string): string => {
  if (!category) return "DOT Inspection Document";
  return category;
};

export interface PendingFile {
  id: string;
  file?: File;
  existingDocId?: number;
  fileName: string;
  fileSize?: number;
  documentCategory: string;
  url?: string;
  contentType?: string;
}

interface DotInspectionPendingFilesSectionProps {
  pendingFiles: PendingFile[];
  onChange: (files: PendingFile[]) => void;
}

const DotInspectionPendingFilesSection: React.FC<
  DotInspectionPendingFilesSectionProps
> = ({ pendingFiles, onChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
    contentType: string;
    revoke: boolean;
  } | null>(null);

  const handleClose = () => {
    setSelectedFile(null);
    setDragOver(false);
    setDialogOpen(false);
    setFileError(null);
  };

  const validateAndSetFile = (file: File) => {
    if (!isFileTypeAllowed(file)) {
      setFileError(`File type "${getExtension(file.name)}" is not supported.`);
      setSelectedFile(null);
    } else {
      setFileError(null);
      setSelectedFile(file);
    }
  };

  const handleAdd = () => {
    if (!selectedFile) return;
    const pending: PendingFile = {
      id: `new-${Date.now()}-${Math.random()}`,
      file: selectedFile,
      fileName: selectedFile.name,
      fileSize: selectedFile.size,
      documentCategory: "",
      contentType: selectedFile.type,
    };
    onChange([...pendingFiles, pending]);
    handleClose();
  };

  const handleRemove = (id: string) => {
    onChange(pendingFiles.filter((f) => f.id !== id));
  };

  const handlePreview = (pf: PendingFile) => {
    if (pf.file) {
      setPreviewFile({
        url: URL.createObjectURL(pf.file),
        name: pf.fileName,
        contentType: pf.contentType || pf.file.type,
        revoke: true,
      });
    } else if (pf.url) {
      setPreviewFile({
        url: pf.url,
        name: pf.fileName,
        contentType: pf.contentType || "",
        revoke: false,
      });
    }
  };

  const handleClosePreview = () => {
    if (previewFile?.revoke) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSetFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) validateAndSetFile(file);
    e.target.value = "";
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Documents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Attach files to this inspection. They will be uploaded when you save.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          Add Documents
        </Button>
      </Box>

      {pendingFiles.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <FileIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No files added yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Add Documents" to attach inspection documents or photos.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>File</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingFiles.map((pf) => (
                <TableRow key={pf.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AttachFileIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {pf.fileName}
                      </Typography>
                      {pf.existingDocId && (
                        <Chip label="Existing" size="small" color="info" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCategory(pf.documentCategory)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {pf.fileSize ? formatFileSize(pf.fileSize) : "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                      {isPreviewableFile(pf.contentType || "") && (
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handlePreview(pf)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemove(pf.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Add Documents</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="subtitle2" gutterBottom>
            Select File
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Supports documents, images, video, audio, and archives
          </Typography>
          {fileError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFileError(null)}>
              {fileError}
            </Alert>
          )}
          {!selectedFile ? (
            <Paper
              sx={{
                border: `2px dashed ${dragOver ? "primary.main" : "grey.300"}`,
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: dragOver ? "action.hover" : "background.paper",
                "&:hover": { borderColor: "primary.main", backgroundColor: "action.hover" },
              }}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
              onDrop={handleDrop}
              onClick={() => document.getElementById("dot-pending-file-input")?.click()}
            >
              <UploadIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                Drop file here or click to browse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supports PDF, images, documents and other file types
              </Typography>
              <input
                id="dot-pending-file-input"
                type="file"
                hidden
                onChange={handleFileInput}
                accept={ALLOWED_EXTENSIONS.join(",")}
              />
            </Paper>
          ) : (
            <Paper sx={{ p: 2, border: "1px solid", borderColor: "success.main" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AttachFileIcon color="success" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setSelectedFile(null)} color="error">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedFile}
            startIcon={<AttachFileIcon />}
            onClick={handleAdd}
          >
            Add to List
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!previewFile} onClose={handleClosePreview} maxWidth="md" fullWidth>
        {previewFile && (
          <>
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" noWrap sx={{ pr: 2 }}>
                {previewFile.name}
              </Typography>
              <IconButton size="small" onClick={handleClosePreview}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "grey.900",
                minHeight: 300,
                p: 2,
              }}
            >
              {isImageFile(previewFile.contentType) && (
                <Box
                  component="img"
                  src={previewFile.url}
                  alt={previewFile.name}
                  sx={{ maxWidth: "100%", maxHeight: "70vh", objectFit: "contain" }}
                />
              )}
              {isVideoFile(previewFile.contentType) && (
                <Box
                  component="video"
                  src={previewFile.url}
                  controls
                  autoPlay
                  sx={{ maxWidth: "100%", maxHeight: "70vh" }}
                />
              )}
              {isPdfFile(previewFile.contentType) && (
                <Box
                  component="iframe"
                  src={previewFile.url}
                  title={previewFile.name}
                  sx={{ width: "100%", height: "70vh", border: "none", backgroundColor: "white" }}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClosePreview}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default DotInspectionPendingFilesSection;
